import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { ScreenListItem } from '@seencast/shared';
import { Button, Badge, Loading } from '../../components/ui.tsx';
import { Modal } from '../../components/Modal.tsx';
import { ApiError } from '../../api/client.ts';
import {
  useRequestSnapshot,
  useSnapshots,
  snapshotImageUrl,
  type Snapshot,
} from '../../api/snapshots.ts';
import { formatRelative } from '../../lib/format.ts';

// How long to wait for a freshly requested capture before showing the offline hint.
const WAIT_MS = 90_000;
const POLL_MS = 5_000;

/** Camera-equivalent glyph (no emoji) — matches the round-stroke icon family. */
function CameraGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13" r="3.2" />
    </svg>
  );
}

function kindBadge(kind: Snapshot['kind']) {
  return kind === 'native' ? (
    <Badge tone="info">Device capture</Badge>
  ) : (
    <Badge tone="neutral">Render (browser player, approximate)</Badge>
  );
}

/**
 * "Live snapshot" section for the screen detail drawer. Request a capture, poll
 * for it, and show the newest frame plus a small history strip. Rendered only for
 * users who can manage the screen (the caller gates on that).
 */
export function ScreenSnapshots({ screen }: { screen: ScreenListItem }) {
  const [phase, setPhase] = useState<'idle' | 'waiting' | 'timeout'>('idle');
  const requestStartRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [enlarge, setEnlarge] = useState<Snapshot | null>(null);

  const request = useRequestSnapshot();
  // Always load existing history; poll only while waiting for a new capture.
  const snapshots = useSnapshots(screen.id, true, phase === 'waiting' ? POLL_MS : false);
  const rows = snapshots.data ?? [];
  const newest = rows[0];

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };
  useEffect(() => clearTimer, []);

  // A capture newer than the request instant means the screen responded — stop waiting.
  useEffect(() => {
    if (phase === 'waiting' && newest && newest.taken_at >= requestStartRef.current) {
      clearTimer();
      setPhase('idle');
    }
  }, [phase, newest]);

  function onRequest() {
    requestStartRef.current = Date.now();
    setPhase('waiting');
    clearTimer();
    timerRef.current = setTimeout(() => setPhase('timeout'), WAIT_MS);
    request.mutate(screen.id, {
      onError: (e) => {
        clearTimer();
        setPhase('idle');
        toast.error(e instanceof ApiError ? e.message : 'Could not request a snapshot');
      },
    });
  }

  return (
    <>
      <div className="divider" />
      <div className="card-title row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
        <span className="a8-snap-glyph" aria-hidden>
          <CameraGlyph />
        </span>
        Live snapshot
      </div>

      <div className="mb-4">
        <Button onClick={onRequest} disabled={phase === 'waiting' || request.isPending}>
          {phase === 'waiting' ? 'Requesting…' : 'Request snapshot'}
        </Button>
      </div>

      {snapshots.isLoading ? (
        <Loading />
      ) : (
        <>
          {phase === 'waiting' && (
            <div className="row text-dim mb-4" role="status">
              <span className="spinner sm" aria-hidden />
              Asking the screen to capture what it&apos;s showing…
            </div>
          )}

          {phase === 'timeout' && !newest && (
            <p className="hint-box warn">
              The screen may be offline, or its player doesn&apos;t support snapshots for the current
              content — the Android player captures everything.
            </p>
          )}

          {newest ? (
            <figure className="a8-snap-figure">
              <img
                className="a8-snap-img"
                src={snapshotImageUrl(screen.id, newest.id)}
                alt={`Latest snapshot of ${screen.name}`}
                width={newest.width}
                height={newest.height}
              />
              <figcaption className="a8-snap-cap">
                {kindBadge(newest.kind)}
                <span className="text-dim">{formatRelative(newest.taken_at)}</span>
              </figcaption>
            </figure>
          ) : phase !== 'timeout' ? (
            <p className="text-dim">No snapshots yet. Request one to see what this screen is showing.</p>
          ) : null}

          {rows.length > 1 && (
            <>
              <div className="a8-snap-strip-label text-dim">Recent captures</div>
              <div className="a8-snap-strip">
                {rows.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="a8-snap-thumb"
                    aria-label={`Snapshot from ${formatRelative(s.taken_at)}`}
                    onClick={() => setEnlarge(s)}
                  >
                    <img src={snapshotImageUrl(screen.id, s.id)} alt="" />
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {enlarge && (
        <Modal title={`Snapshot · ${formatRelative(enlarge.taken_at)}`} onClose={() => setEnlarge(null)} size="lg">
          <div className="a8-snap-enlarge">
            <img src={snapshotImageUrl(screen.id, enlarge.id)} alt={`Snapshot of ${screen.name}`} />
            <div className="row mt-2" style={{ gap: 'var(--sp-2)' }}>
              {kindBadge(enlarge.kind)}
              <span className="text-dim">
                {enlarge.width}×{enlarge.height}
              </span>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
